import { ScanLine, FilePen, PenTool, Mail } from "lucide-react";

/**
 * ProcessStrip — visual "how it works in 3 minutes" strip used on the public
 * landings and inside the SEO articles. Emphasises the digital, fast and
 * simple nature of the flow. Pure icons + text — no external image deps so
 * it renders identically on prerender and on the client (avoids CLS).
 */
const STEPS = [
  {
    icon: ScanLine,
    title: "Scan your ID",
    desc: "Snap a photo. We auto-fill your name and address from your driver's license.",
    accent: "00:30",
  },
  {
    icon: FilePen,
    title: "Fill the digital form",
    desc: "VIN decodes the vehicle automatically. Add price, odometer and as-is terms.",
    accent: "01:30",
  },
  {
    icon: PenTool,
    title: "Sign online",
    desc: "Sign with your finger or mouse. Send a secure link to the other party.",
    accent: "02:30",
  },
  {
    icon: Mail,
    title: "PDF delivered",
    desc: "Both parties get the signed, encrypted PDF copy by email. Done.",
    accent: "03:00",
  },
];

export default function ProcessStrip({ title = "From scan to signed in 3 minutes", lede = "Everything happens on your phone or laptop — no printing, no scanning to PDF, no email back-and-forth." }) {
  return (
    <section className="max-w-7xl mx-auto px-6 py-16" data-testid="process-strip">
      <div className="max-w-2xl mb-10">
        <span className="text-xs uppercase tracking-widest font-semibold text-[var(--csd-primary)]">3-minute flow</span>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2 tracking-tight">{title}</h2>
        <p className="mt-4 text-[var(--csd-text-secondary)] leading-relaxed">{lede}</p>
      </div>

      <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Dotted connector line on desktop */}
        <div className="hidden lg:block absolute top-14 left-12 right-12 border-t-2 border-dashed border-[var(--csd-primary-light)] pointer-events-none" aria-hidden="true" />
        {STEPS.map((s, i) => (
          <div key={i} className="csd-card p-6 relative bg-white" data-testid={`process-step-${i}`}>
            <div className="relative flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-[var(--csd-primary)] grid place-items-center text-white shadow-md shrink-0">
                <s.icon size={22} strokeWidth={2.2} />
              </div>
              <div className="ml-auto text-xs font-mono font-semibold text-[var(--csd-text-secondary)] bg-[var(--csd-primary-light)]/60 px-2 py-1 rounded-md tabular-nums">
                {s.accent}
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--csd-text-secondary)] mb-1">Step {i + 1}</div>
            <div className="font-heading font-bold text-lg leading-snug">{s.title}</div>
            <div className="text-sm text-[var(--csd-text-secondary)] mt-2 leading-relaxed">{s.desc}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
